import type { paths } from "@src/packages/openapi/schema";

type CyclesResponse =
  paths["/{tenantId}/patients/{patientId}/cycles"]["get"]["responses"][200]["content"]["application/json"];

export type CanonicalCycle = CyclesResponse["data"][number];
export type CanonicalCycleType = CanonicalCycle["cycleType"];

export type CanonicalCycleIdentity = {
  cycleType: CanonicalCycleType;
  startDate?: string | null;
  clinic?: string | null;
  provider?: string | null;
};

/**
 * Normalize user-entered identity values without changing the value that is
 * ultimately persisted. This is only for matching likely existing records.
 */
export function normalizeCycleIdentityValue(
  value: string | null | undefined,
): string | null {
  const normalized = value?.trim().replace(/\s+/g, " ").toLocaleLowerCase();
  return normalized ? normalized : null;
}

function calendarMonth(value: string | null | undefined): string | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})/.exec(value);
  return match ? `${match[1]}-${match[2]}` : null;
}

function datesMatchApproximately(
  left: string | null | undefined,
  right: string | null | undefined,
): boolean {
  if (!left || !right) return false;
  return left === right || calendarMonth(left) === calendarMonth(right);
}

function clinicMatches(
  identity: CanonicalCycleIdentity,
  cycle: CanonicalCycle,
): boolean {
  const clinic = normalizeCycleIdentityValue(identity.clinic);
  const provider = normalizeCycleIdentityValue(identity.provider);
  const existingProvider = normalizeCycleIdentityValue(cycle.provider);

  // The current Cycle response exposes the provider/facility as free text;
  // keep the requested clinic/provider aliases equivalent until the API adds
  // a canonical clinic relationship.
  if (clinic && clinic !== existingProvider) return false;
  if (provider && provider !== existingProvider) return false;
  return Boolean(existingProvider);
}

/**
 * Return candidates that share the known identity facts. Callers should show
 * these as a review/selection step when more than one candidate exists.
 * Missing facts intentionally do not match: an unknown clinic or date must
 * not cause two legitimate repeat cycles to collapse into one.
 */
export function findLikelyCycleMatches(
  cycles: readonly CanonicalCycle[],
  identity: CanonicalCycleIdentity,
): CanonicalCycle[] {
  const hasDate = Boolean(identity.startDate);
  const hasClinic = Boolean(
    normalizeCycleIdentityValue(identity.clinic) ||
    normalizeCycleIdentityValue(identity.provider),
  );

  if (!hasDate || !hasClinic) return [];

  return cycles.filter((cycle) => {
    if (cycle.cycleType !== identity.cycleType) return false;
    if (!datesMatchApproximately(identity.startDate, cycle.startDate)) {
      return false;
    }
    return clinicMatches(identity, cycle);
  });
}

/**
 * A single exact-enough match may be reused automatically. Multiple matches
 * remain a user decision so repeat treatment cycles are never silently merged.
 */
export function resolveSingleLikelyCycle(
  cycles: readonly CanonicalCycle[],
  identity: CanonicalCycleIdentity,
): CanonicalCycle | null {
  const matches = findLikelyCycleMatches(cycles, identity);
  return matches.length === 1 ? (matches[0] ?? null) : null;
}
